import {inject, Injectable} from "@angular/core";
import {map, Observable} from "rxjs";
import {AssetValuation} from "../models/asset-valuation/asset-valuation";
import {
    AssetApiService,
    AssetValuationHistoryBaseRequest,
    QuickForecastMode,
    QuickForecastSeeds,
    SaveForecastWithHistoryBaseResponse
} from "../generated-sources/api";
import {AssetValuationScenario} from "../models/asset-valuation/asset-valuation-scenario";
import {DiscountMultipleValuation} from "../models/asset-valuation/discount-multiple-valuation";
import {FinancialPerformanceRecord} from "../models/asset/financial-performance-record";
import {FinancialOperationalValuation} from "../models/asset-valuation/financial-operational-valuation";
import {AssetValuationWithScenarios} from "../models/asset-valuation/asset-valuation-with-scenario";

@Injectable({
    providedIn: "root"
})
export class AssetValuationService {

    private readonly assetApiService = inject(AssetApiService);

    constructor() {
    }

    public getAssetValuationsForAssetAndFundValuation(assetId: number, fundValuationId: number): Observable<AssetValuation[]> {
        return this.assetApiService.getAssetValuationsForAssetAndFundValuation(assetId, fundValuationId);
    }

    public getAssetValuationDetail(assetId: number, assetValuationId: number): Observable<AssetValuationWithScenarios> {
        return this.assetApiService.getAssetValuation(assetId, assetValuationId);
    }

    public updatePreferedValuation(assetId: number, assetValuationId: number): Observable<AssetValuation[]> {
        return this.assetApiService.updatePreferredValuation(assetId, assetValuationId);
    }

    /** US-1189: records the user's confirmation that a valuation without forecast cashflow is a write-off. */
    public confirmWriteoff(assetId: number, assetValuationId: number, confirmed: boolean): Observable<AssetValuation> {
        return this.assetApiService.confirmAssetValuationWriteoff(assetId, assetValuationId, confirmed);
    }

    public save(assetId: number, fundValuationId: number, assetValuationId: number, avWithScenarios: AssetValuationWithScenarios): Observable<AssetValuationWithScenarios> {
        return this.assetApiService.updateAssetValuation(assetId, assetValuationId, fundValuationId, avWithScenarios);
    }

    public create(assetId: number, fundValuationId: number, assetValuation: AssetValuation): Observable<AssetValuationWithScenarios> {
        return this.assetApiService.createAssetValuation(assetId, fundValuationId, assetValuation);
    }

    public saveRationale(assetId: number, assetValuationId: number, fundValuationId: number, rationale: string): Observable<AssetValuation> {
        return this.assetApiService.updateAssetValuationRationale(assetId, assetValuationId, fundValuationId, rationale);
    }

    public delete(assetId: number, assetValuationId: number): Observable<number> {
        return this.assetApiService.deleteAssetValuation(assetId, assetValuationId);
    }

    public saveScenario(assetId: number, assetValuationId: number, scenario: AssetValuationScenario): Observable<AssetValuationScenario> {
        const result = !!scenario.id && !!assetId && !!assetValuationId
            ? this.assetApiService.updateAssetValuationScenario(assetId, assetValuationId, scenario.id, scenario)
            : this.assetApiService.createAssetValuationScenario(assetId, assetValuationId, scenario);
        return result.pipe(map((scenarioDto) => scenarioDto));
    }

    public saveScenariosForAssetValuation(assetId: number, assetValuationId: number, scenarios: AssetValuationScenario[]): Observable<AssetValuationScenario[]> {
        return this.assetApiService.updateScenariosForAssetValuation(assetId, assetValuationId, scenarios);
    }

    public calcDMV(assetId: number, assetValuationId: number, dmv: DiscountMultipleValuation, withAlgorithm: boolean): Observable<DiscountMultipleValuation> {
        return this.assetApiService.calculateDiscountMultipleValuation(assetId, assetValuationId, withAlgorithm, dmv);
    }

    public calcFOV(assetId: number, assetValuationId: number, operationalValuation: FinancialOperationalValuation): Observable<FinancialOperationalValuation> {
        return this.assetApiService.calculateFinancialOperationalValuation(assetId, assetValuationId, operationalValuation);
    }

    public getFinancialPerformanceForScenario(assetId: number, assetValuationId: number, scenarioId: number): Observable<FinancialPerformanceRecord[]> {
        return this.assetApiService.getFinancialPerformanceForScenario(assetId, assetValuationId, scenarioId);
    }

    public calculateFinancialPerformanceForScenario(
        assetId: number,
        assetValuationId: number,
        scenarioId: number,
        financialPerformanceRecords: FinancialPerformanceRecord[],
        inputMode?: "RELATIVE" | "ABSOLUTE",
        historyBase?: AssetValuationHistoryBaseRequest
    ): Observable<FinancialPerformanceRecord[]> {
        return this.assetApiService.calculateFinancialPerformanceForScenario(
            assetId, assetValuationId, scenarioId, {financialPerformanceRecords, historyBase}, inputMode
        );
    }

    public calculateQuickForecast(
        assetId: number,
        assetValuationId: number,
        scenarioId: number,
        mode: QuickForecastMode,
        earningBase: string,
        seeds: QuickForecastSeeds,
        financialPerformanceRecords: FinancialPerformanceRecord[],
        historyBase?: AssetValuationHistoryBaseRequest
    ): Observable<FinancialPerformanceRecord[]> {
        return this.assetApiService.calculateQuickForecastForScenario(
            assetId, assetValuationId, scenarioId,
            {mode, earningBase, seeds, financialPerformanceRecords, historyBase}
        );
    }

    public copyFinancialPerformanceFromScenario(
        assetId: number,
        assetValuationId: number,
        targetScenarioId: number,
        sourceScenarioId: number): Observable<FinancialPerformanceRecord[]> {
        return this.assetApiService.copyFinancialPerformanceFromScenario(assetId, assetValuationId, targetScenarioId, sourceScenarioId);
    }

    public resetValuation(assetId: number, assetValuationId: number): Observable<AssetValuationWithScenarios> {
        return this.assetApiService.resetAssetValuation(assetId, assetValuationId);
    }

    public saveFinancialPerformanceWithHistoryBase(
        assetId: number,
        assetValuationId: number,
        scenarioId: number,
        financialPerformanceRecords: FinancialPerformanceRecord[],
        inputMode?: "RELATIVE" | "ABSOLUTE",
        historyBase?: AssetValuationHistoryBaseRequest
    ): Observable<SaveForecastWithHistoryBaseResponse> {
        return this.assetApiService.saveFinancialPerformanceWithHistoryBase(
            assetId, assetValuationId, scenarioId, {financialPerformanceRecords, historyBase}, inputMode
        );
    }
}
